import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, type Persona } from '../lib/auth';
import { ErrorNote, Spinner } from '../ui/components';

const LOGIN_ERRORS: Record<string, string> = {
  no_access:
    'You signed in with One Citizen, but this account has no Justice-portal staff access yet. Ask your registry or chambers administrator to grant you a role.',
  access_denied: 'One Citizen sign-in was cancelled.',
  idp_unreachable: 'One Citizen is not reachable right now — try again shortly.',
  session_expired: 'The sign-in attempt expired — please try again.',
  state_mismatch: 'The sign-in attempt could not be validated — please try again.',
  token_exchange_failed: 'One Citizen rejected the sign-in — please try again.',
  signin_failed: 'Sign-in failed — please try again.',
};

interface WorkspaceGroup {
  key: string;
  title: string;
  blurb: string;
  roles: string[];
}

const WORKSPACE_GROUPS: WorkspaceGroup[] = [
  {
    key: 'judiciary',
    title: 'Judiciary',
    blurb: 'Case registry, e-filing desk, hearing schedules, cause lists, and judgment delivery across all 6 divisions.',
    roles: ['REGISTRAR_COURT', 'COURT_CLERK', 'JUDGE', 'TRIBUNAL_MEMBER'],
  },
  {
    key: 'ag',
    title: "Attorney General's Chambers",
    blurb: 'Legislation registry & consolidation, the Government Gazette, bill drafting, legal opinions, and litigation watch-list.',
    roles: ['LAW_REVISION_COMMISSIONER', 'GAZETTE_EDITOR', 'LEGISLATIVE_DRAFTER'],
  },
];

const ROLE_LABEL: Record<string, string> = {
  REGISTRAR_COURT: 'Registrar',
  COURT_CLERK: 'Court Clerk',
  JUDGE: 'Judge',
  TRIBUNAL_MEMBER: 'Tribunal Member',
  LAW_REVISION_COMMISSIONER: 'Law Revision Commissioner',
  GAZETTE_EDITOR: 'Gazette Editor',
  LEGISLATIVE_DRAFTER: 'Legislative Drafter',
};

function primaryGroup(p: Persona): WorkspaceGroup {
  return WORKSPACE_GROUPS.find((g) => p.roles.some((r) => g.roles.includes(r))) ?? WORKSPACE_GROUPS[0]!;
}

export function Login() {
  const { personas, config, login, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loginError = params.get('login_error');
  const loginErrorText = loginError ? (LOGIN_ERRORS[loginError] ?? LOGIN_ERRORS.signin_failed) : null;

  const enter = async (p: Persona) => {
    setBusy(p.id);
    setError(null);
    try {
      await login(p.id);
      navigate('/');
    } catch {
      setError('Sign-in failed — is the gateway running?');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-ink-950">
      <div className="mx-auto max-w-5xl px-6 py-12">
        <header className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 text-2xl font-black text-white">
            SC
          </div>
          <h1 className="text-2xl font-black tracking-tight text-white">One Gov Justice &amp; Legal Affairs</h1>
          <p className="mt-1 text-sm text-slate-400">
            Republic of Seychelles — Judiciary case management and Attorney General &amp; Legislation staff portal
          </p>
        </header>

        {/* Law of Seychelles is a genuinely public record — no sign-in needed
            to browse it, so it is surfaced above the staff sign-in options,
            not buried inside them. */}
        <Link
          to="/law"
          className="mx-auto mb-10 flex max-w-xl items-center gap-4 rounded-2xl border border-amber-400/30 bg-gradient-to-r from-amber-500/10 to-amber-400/5 px-5 py-4 text-left transition hover:border-amber-400/60 hover:from-amber-500/20"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-400/20 text-xl">⚖</span>
          <span>
            <span className="block text-sm font-bold text-amber-200">Search the Law of Seychelles — no sign-in required</span>
            <span className="mt-0.5 block text-xs text-amber-100/70">
              Browse consolidated Acts &amp; subsidiary legislation, search by section, resolve a citation, and read the Government
              Gazette. Public record, open to everyone.
            </span>
          </span>
          <span className="ml-auto shrink-0 text-amber-300">→</span>
        </Link>

        {loginErrorText ? (
          <div className="mx-auto mb-6 max-w-xl">
            <ErrorNote message={loginErrorText} />
          </div>
        ) : null}
        {error ? (
          <div className="mx-auto mb-6 max-w-xl">
            <ErrorNote message={error} />
          </div>
        ) : null}

        {config.oneCitizen ? (
          <div className="mx-auto mb-10 max-w-md text-center">
            <a
              href="/auth/onecitizen/start"
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-brand-600 px-5 py-3.5 text-base font-bold text-white shadow-lg shadow-brand-600/25 transition hover:bg-brand-700"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-xs font-black">1</span>
              Sign in with One Citizen
            </a>
            <p className="mt-2 text-xs text-slate-500">
              Your national digital identity. Staff roles are granted by your registry or chambers administrator.
            </p>
          </div>
        ) : null}

        {loading ? <Spinner label="Loading sign-in options…" /> : null}

        {config.devLogin && personas.length > 0 ? (
          <p className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-amber-400/90">
            Development personas — disabled in production (ONEGOV_DEV_LOGIN=0)
          </p>
        ) : null}

        {WORKSPACE_GROUPS.filter(() => config.devLogin).map((group) => {
          const items = personas.filter((p) => primaryGroup(p).key === group.key);
          if (items.length === 0) return null;
          return (
            <section key={group.key} className="mb-8">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300">{group.title}</h2>
              <p className="mb-3 mt-0.5 text-xs text-slate-500">{group.blurb}</p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => enter(p)}
                    disabled={busy !== null}
                    className="rounded-xl border border-ink-700 bg-ink-900 p-4 text-left transition hover:border-brand-500 hover:bg-ink-800 disabled:opacity-60"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">{p.name}</span>
                      {busy === p.id ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-500 border-t-white" />
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-xs font-medium text-brand-100">{p.title}</div>
                    <div className="mt-1 text-xs text-slate-400">{p.org}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.roles.map((r) => (
                        <span key={r} className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 ring-1 ring-ink-700">
                          {ROLE_LABEL[r] ?? r.replaceAll('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
